
import { useState, useMemo } from 'react';
import { myAiProjects, statusConfig, ProjectStatus, MyAIProject } from '../../../mocks/myAiProjects';

type FilterStatus = 'all' | ProjectStatus;

const MyAIProjectsTab = () => {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'views'>('newest');
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const filteredProjects = useMemo(() => {
    let projects = filterStatus === 'all'
      ? [...myAiProjects]
      : myAiProjects.filter(p => p.status === filterStatus);

    if (sortBy === 'newest') {
      projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'oldest') {
      projects.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
    } else {
      projects.sort((a, b) => b.views - a.views);
    }

    return projects;
  }, [filterStatus, sortBy]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: myAiProjects.length };
    myAiProjects.forEach(p => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return counts;
  }, []);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleDelete = (projectId: string) => {
    setDeleteConfirm(null);
    showNotification('Project deleted successfully', 'success');
  };

  const handleDuplicate = (project: MyAIProject) => {
    showNotification(`"${project.title}" duplicated as draft`, 'success');
  };

  const handleSubmit = (project: MyAIProject) => {
    showNotification(`"${project.title}" submitted for review`, 'success');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="space-y-5">
      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center gap-2 transition-all duration-300 ${
          notification.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
        }`}>
          <i className={notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          {notification.message}
        </div>
      )}

      {/* Header with stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-movie-2-line text-amber-500"></i>
            My AI Projects
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">{myAiProjects.length} projects total</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer bg-white"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="views">Most Views</option>
          </select>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'draft', 'submitted', 'in_review', 'approved', 'published', 'rejected'] as FilterStatus[]).map((status) => {
          const count = statusCounts[status] || 0;
          if (status !== 'all' && count === 0) return null;
          const config = status === 'all'
            ? { label: 'All', color: 'text-gray-700', bgColor: 'bg-gray-100', icon: 'ri-apps-line' }
            : statusConfig[status];
          return (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterStatus === status
                  ? 'bg-amber-500 text-white shadow-sm'
                  : `${config.bgColor} ${config.color} hover:opacity-80`
              }`}
            >
              <i className={`${config.icon} mr-1.5`}></i>
              {config.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                filterStatus === status ? 'bg-white/20 text-white' : 'bg-black/5 text-current'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Projects list */}
      {filteredProjects.length > 0 ? (
        <div className="space-y-3">
          {filteredProjects.map((project) => {
            const config = statusConfig[project.status];
            const isExpanded = expandedProject === project.id;

            return (
              <div
                key={project.id}
                className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-all"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Thumbnail */}
                  <div className="sm:w-48 h-32 sm:h-auto flex-shrink-0 relative">
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${config.bgColor} ${config.color} backdrop-blur-sm`}>
                        <i className={`${config.icon} mr-1`}></i>
                        {config.label}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-sm truncate">{project.title}</h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{project.category}</span>
                          <span>Updated {formatDate(project.updatedAt)}</span>
                        </div>
                      </div>

                      {/* Actions dropdown */}
                      <div className="relative flex-shrink-0">
                        <button
                          onClick={() => setExpandedProject(isExpanded ? null : project.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-gray-500"
                        >
                          <i className="ri-more-2-fill"></i>
                        </button>

                        {isExpanded && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setExpandedProject(null)}></div>
                            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                              {project.status === 'published' && (
                                <button className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                  <i className="ri-external-link-line mr-2.5 text-gray-400"></i>
                                  View Live
                                </button>
                              )}
                              {(project.status === 'draft' || project.status === 'rejected') && (
                                <button className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                  <i className="ri-edit-line mr-2.5 text-gray-400"></i>
                                  Edit Project
                                </button>
                              )}
                              {project.status === 'draft' && (
                                <button
                                  onClick={() => { handleSubmit(project); setExpandedProject(null); }}
                                  className="flex items-center w-full px-3 py-2 text-sm text-sky-600 hover:bg-sky-50 cursor-pointer"
                                >
                                  <i className="ri-send-plane-line mr-2.5"></i>
                                  Submit for Review
                                </button>
                              )}
                              <button
                                onClick={() => { handleDuplicate(project); setExpandedProject(null); }}
                                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-file-copy-line mr-2.5 text-gray-400"></i>
                                Duplicate
                              </button>
                              <div className="border-t border-gray-100 my-1"></div>
                              <button
                                onClick={() => { setDeleteConfirm(project.id); setExpandedProject(null); }}
                                className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                              >
                                <i className="ri-delete-bin-line mr-2.5"></i>
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 mt-2 line-clamp-2 leading-relaxed">{project.description}</p>

                    {/* Tools */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {project.tools.slice(0, 3).map((tool) => (
                        <span key={tool} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] rounded font-medium">
                          {tool}
                        </span>
                      ))}
                      {project.tools.length > 3 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">
                          +{project.tools.length - 3}
                        </span>
                      )}
                    </div>

                    {/* Stats for published projects */}
                    {project.status === 'published' && (
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatNumber(project.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line"></i>
                          {formatNumber(project.likes)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {formatNumber(project.comments)}
                        </span>
                        {project.publishedAt && (
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            Published {formatDate(project.publishedAt)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Rejection reason */}
                    {project.status === 'rejected' && project.rejectionReason && (
                      <div className="mt-3 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                        <p className="text-xs text-red-700 flex items-start gap-1.5">
                          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                          <span>{project.rejectionReason}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-movie-2-line text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Projects Found</h3>
          <p className="text-gray-500 text-sm">
            {filterStatus === 'all'
              ? 'Start creating your first AI drama project!'
              : `No projects with "${statusConfig[filterStatus as ProjectStatus]?.label}" status.`}
          </p>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Project?</h3>
            <p className="text-sm text-gray-600 text-center mb-5">
              This action cannot be undone. The project and all its data will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAIProjectsTab;
